import {
  CurrencyConfig,
  getCurrencyConfig,
} from "../config/currencyConfig";
import { API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./apiClient";

export interface StakeConfig {
  minBet: number;
  maxBet: number;
  stakes: number[];
}

export type StakeSettings = Record<string, StakeConfig>;

export interface LoadedGameConfig {
  currency?: string;
  stakeSettings: StakeSettings;
}

export const DEFAULT_STAKE_SETTINGS: StakeSettings = {
  PKR: {
    minBet: 50,
    maxBet: 50000,
    stakes: [50, 200, 500, 1000, 1000, 50000],
  },
  USD: {
    minBet: 0.5,
    maxBet: 5000,
    stakes: [0.5, 5, 10, 50, 100, 500],
  },
  INR: {
    minBet: 10,
    maxBet: 25000,
    stakes: [10, 50, 100, 500, 1000, 25000],
  },
  DEFAULT: {
    minBet: 50,
    maxBet: 50000,
    stakes: [50, 200, 500, 1000, 1000, 50000],
  },
};

const normalizeCurrency = (currency?: string | null) =>
  currency?.trim().toUpperCase() || "DEFAULT";

const toNumber = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.NaN;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  return Number.NaN;
};

const readString = (
  source: Record<string, unknown>,
  keys: string[],
): string | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }

  return null;
};

const readNumber = (
  source: Record<string, unknown>,
  keys: string[],
): number => {
  for (const key of keys) {
    const parsed = toNumber(source[key]);
    if (Number.isFinite(parsed)) return parsed;
  }

  return Number.NaN;
};

const toNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.map(toNumber).filter(Number.isFinite);
  }

  if (typeof value === "string") {
    return value.split(",").map(toNumber).filter(Number.isFinite);
  }

  return [];
};

const readNumberArray = (
  source: Record<string, unknown>,
  keys: string[],
): number[] => {
  for (const key of keys) {
    const parsed = toNumberArray(source[key]);
    if (parsed.length) return parsed;
  }

  return [];
};

const mapStakeConfig = (source: unknown): StakeConfig | null => {
  if (!source || typeof source !== "object") return null;

  const item = source as Record<string, unknown>;
  const minBet = readNumber(item, [
    "minBet",
    "min_bet",
    "minimumBet",
    "minimum_bet",
    "min",
  ]);
  const maxBet = readNumber(item, [
    "maxBet",
    "max_bet",
    "maximumBet",
    "maximum_bet",
    "max",
  ]);
  const stakes = readNumberArray(item, [
    "stakes",
    "stake",
    "betValues",
    "bet_values",
    "betAmounts",
    "bet_amounts",
    "values",
  ]);

  if (!Number.isFinite(minBet) || !Number.isFinite(maxBet) || minBet > maxBet) {
    return null;
  }

  return {
    minBet,
    maxBet,
    stakes: stakes.length ? stakes : [minBet, maxBet],
  };
};

const finalizeStakeSettings = (
  settings: StakeSettings,
): StakeSettings | null => {
  const entries = Object.entries(settings);
  if (!entries.length) return null;

  return {
    ...settings,
    DEFAULT:
      settings.DEFAULT || entries[0][1] || DEFAULT_STAKE_SETTINGS.DEFAULT,
  };
};

const mapSingleCurrencyStakeSettings = (
  source: unknown,
): LoadedGameConfig | null => {
  if (!source || typeof source !== "object") return null;

  const item = source as Record<string, unknown>;
  const currency = readString(item, ["currency", "currencyCode", "code"]);
  const config = mapStakeConfig(item.stakeSettings);

  if (!currency || !config) return null;

  const normalizedCurrency = normalizeCurrency(currency);
  return {
    currency: normalizedCurrency,
    stakeSettings: {
      [normalizedCurrency]: config,
      DEFAULT: config,
    },
  };
};

const mapStakeSettings = (source: unknown): StakeSettings | null => {
  if (!source) return null;

  if (Array.isArray(source)) {
    const mapped: StakeSettings = {};

    for (const entry of source) {
      if (!entry || typeof entry !== "object") continue;

      const item = entry as Record<string, unknown>;
      const currency = readString(item, ["currency", "currencyCode", "code"]);
      const config = mapStakeConfig(item);
      if (currency && config) mapped[normalizeCurrency(currency)] = config;
    }

    return finalizeStakeSettings(mapped);
  }

  if (typeof source !== "object") return null;

  const mapped: StakeSettings = {};
  for (const [currency, value] of Object.entries(source)) {
    const config = mapStakeConfig(value);
    if (config) {
      mapped[currency === "DEFAULT" ? "DEFAULT" : normalizeCurrency(currency)] =
        config;
    }
  }

  return finalizeStakeSettings(mapped);
};

export const extractGameConfig = (response: unknown): LoadedGameConfig => {
  const payload = response as Record<string, unknown> | undefined;
  const data = payload?.data as Record<string, unknown> | undefined;
  const directData = mapSingleCurrencyStakeSettings(data);
  if (directData) return directData;

  const directPayload = mapSingleCurrencyStakeSettings(payload);
  if (directPayload) return directPayload;

  const currency =
    (data && readString(data, ["currency", "currencyCode", "code"])) ||
    (payload && readString(payload, ["currency", "currencyCode", "code"])) ||
    undefined;
  const candidates = [
    payload?.stakeSettings,
    payload?.currencies,
    data?.stakeSettings,
    data?.currencies,
    payload?.data,
    response,
  ];

  for (const candidate of candidates) {
    const stakeSettings = mapStakeSettings(candidate);
    if (stakeSettings) {
      return {
        currency: currency ? normalizeCurrency(currency) : undefined,
        stakeSettings,
      };
    }
  }

  return { currency, stakeSettings: DEFAULT_STAKE_SETTINGS };
};

export const loadGameConfig = async (): Promise<LoadedGameConfig> => {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.gameConfig,
      {},
      {
        timeout: 10000,
      },
    );

    return extractGameConfig(response.data);
  } catch {
    const storedCurrency = localStorage.getItem("currency");
    return {
      currency: storedCurrency ? normalizeCurrency(storedCurrency) : undefined,
      stakeSettings: DEFAULT_STAKE_SETTINGS,
    };
  }
};

export const getStakeConfig = (
  settings: StakeSettings,
  currency?: string,
): StakeConfig =>
  settings[normalizeCurrency(currency)] ||
  settings.DEFAULT ||
  DEFAULT_STAKE_SETTINGS.DEFAULT;

export const createCurrencyConfig = (
  currency: string,
  stakeConfig: StakeConfig,
): CurrencyConfig => {
  const normalizedCurrency = normalizeCurrency(currency);
  const displayConfig = getCurrencyConfig(normalizedCurrency);
  const hasDecimals = [
    stakeConfig.minBet,
    stakeConfig.maxBet,
    ...stakeConfig.stakes,
  ].some((value) => !Number.isInteger(value));

  return {
    ...displayConfig,
    code: normalizedCurrency === "DEFAULT" ? displayConfig.code : normalizedCurrency,
    symbol:
      displayConfig.code === normalizedCurrency
        ? displayConfig.symbol
        : normalizedCurrency,
    name:
      displayConfig.code === normalizedCurrency
        ? displayConfig.name
        : normalizedCurrency,
    minBet: stakeConfig.minBet,
    maxBet: stakeConfig.maxBet,
    stakes: stakeConfig.stakes,
    stepAmount: normalizedCurrency === "USD" ? 1 : 10,
    decimals: hasDecimals ? 2 : 0,
  };
};
